import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Calendar, Users, Target } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { useLanguage } from "../contexts/LanguageContext";
import api from "../lib/api";

const formatMoney = (amount, currency, lang) => {
  try {
    return new Intl.NumberFormat(lang === "en" ? "en-US" : "fr-FR", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch (_) {
    return `${amount} ${currency}`;
  }
};

const CampaignsPage = () => {
  const { t, lang } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/campaigns")
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const path = lang === "en" ? "/campaigns" : "/campagnes";

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="campaigns-page">
      <Seo
        title={t.seo.campaigns.title}
        description={t.seo.campaigns.description}
        path={path}
        lang={lang}
      />
      <Navbar />

      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-gradient-to-b from-[#002B49] to-[#001F35] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 70% 30%, #C5A059 0%, transparent 50%)" }} />
        <div className="container-narrow relative">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/70 hover:text-[#E8C77E] mb-6 text-sm transition-colors"
            data-testid="campaigns-back-home"
          >
            <ArrowLeft size={14} /> {t.campaignsPage.back}
          </Link>
          <span className="eyebrow text-[#E8C77E]" data-testid="campaigns-eyebrow">{t.campaignsPage.eyebrow}</span>
          <span className="block w-16 h-px bg-[#C5A059] mt-4 mb-6" />
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium leading-[1.1]" data-testid="campaigns-title">
            {t.campaignsPage.title}
          </h1>
          <p className="text-white/85 text-lg md:text-xl mt-6 max-w-2xl leading-relaxed">
            {t.campaignsPage.intro}
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="container-narrow">
          {loading ? (
            <div className="text-center text-[#64748B] py-12" data-testid="campaigns-loading">{t.campaignsPage.loading}</div>
          ) : items.length === 0 ? (
            <div className="text-center text-[#64748B] py-12" data-testid="campaigns-empty">{t.campaignsPage.empty}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {items.map((c, i) => {
                const detailPath = lang === "en" ? `/campaigns/${c.slug}` : `/campagnes/${c.slug}`;
                const completed = (c.percentage || 0) >= 100;
                return (
                  <motion.article
                    key={c.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                    className="group bg-white rounded-2xl overflow-hidden border border-black/5 shadow-[0_8px_40px_-20px_rgba(0,43,73,0.15)] flex flex-col hover:-translate-y-1 transition-transform"
                    data-testid={`campaign-card-${i}`}
                  >
                    <Link to={detailPath} className="block aspect-[16/10] overflow-hidden bg-[#F3F0EA] relative">
                      {c.image_url && (
                        <img
                          src={c.image_url}
                          alt={lang === "en" ? c.title_en : c.title_fr}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                      )}
                      {completed && (
                        <span className="absolute top-3 left-3 bg-[#C5A059] text-white text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full">
                          {t.campaignsPage.completed}
                        </span>
                      )}
                    </Link>
                    <div className="p-6 md:p-7 flex-1 flex flex-col">
                      <h2 className="font-serif text-xl md:text-2xl text-[#002B49] font-semibold leading-tight">
                        {lang === "en" ? c.title_en : c.title_fr}
                      </h2>
                      <p className="text-[#334155] text-sm leading-relaxed mt-3 line-clamp-3">
                        {lang === "en" ? c.description_en : c.description_fr}
                      </p>

                      {/* Progress */}
                      <div className="mt-5">
                        <div className="flex items-end justify-between text-xs text-[#64748B] mb-2">
                          <span className="font-semibold text-[#002B49] text-sm">
                            {formatMoney(c.raised_amount, c.currency, lang)}{" "}
                            <span className="text-[#64748B] font-normal">{t.campaignsPage.raised}</span>
                          </span>
                          <span>{c.percentage}%</span>
                        </div>
                        <div className="h-2 bg-[#F3F0EA] rounded-full overflow-hidden">
                          <motion.div
                            className="h-full bg-gradient-to-r from-[#002B49] to-[#C5A059] rounded-full"
                            initial={{ width: 0 }}
                            whileInView={{ width: `${Math.min(c.percentage, 100)}%` }}
                            viewport={{ once: true }}
                            transition={{ duration: 1.2, ease: "easeOut" }}
                          />
                        </div>
                        <div className="flex items-center justify-between text-xs text-[#64748B] mt-2">
                          <span className="inline-flex items-center gap-1"><Target size={12} /> {t.campaignsPage.of} {formatMoney(c.goal_amount, c.currency, lang)}</span>
                          <span className="inline-flex items-center gap-1"><Users size={12} /> {c.donor_count} {t.campaignsPage.donors}</span>
                        </div>
                        {c.deadline && (
                          <div className="text-xs text-[#64748B] mt-2 inline-flex items-center gap-1">
                            <Calendar size={12} /> {t.campaignsPage.deadline}: {new Date(c.deadline).toLocaleDateString(lang === "en" ? "en-US" : "fr-FR", { day: "2-digit", month: "short", year: "numeric" })}
                          </div>
                        )}
                      </div>

                      <div className="mt-6 flex gap-3 items-center">
                        <Link
                          to={`/?campaign=${c.slug}#donate`}
                          className="flex-1 inline-flex items-center justify-center gap-2 bg-[#002B49] hover:bg-[#001F35] text-white px-4 py-3 rounded-lg text-sm font-semibold transition-colors"
                          data-testid={`campaign-support-${i}`}
                        >
                          {t.campaignsPage.support}
                        </Link>
                        <Link
                          to={detailPath}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#002B49] hover:text-[#C5A059] transition-colors"
                          data-testid={`campaign-detail-${i}`}
                        >
                          {t.campaignsPage.moreDetails}
                          <ArrowRight size={14} />
                        </Link>
                      </div>
                    </div>
                  </motion.article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="py-16 bg-[#002B49] text-white">
        <div className="container-narrow text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-medium mb-3">{t.campaignsPage.ctaTitle}</h2>
          <p className="text-white/80 max-w-xl mx-auto mb-7">{t.campaignsPage.ctaSubtitle}</p>
          <Link
            to="/#donate"
            className="inline-flex items-center justify-center gap-2 bg-[#C5A059] hover:bg-[#B08F4D] text-white px-7 py-3.5 rounded-lg font-semibold transition-colors"
            data-testid="campaigns-cta-free"
          >
            {t.campaignsPage.ctaButton}
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CampaignsPage;
